import { Auth } from 'aws-amplify'
import { CognitoUser } from 'amazon-cognito-identity-js'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@app/context/auth'
import { GetSsoUserDataQuery, Organization } from '@app/generated/graphql'

import { useDfeByUrn } from './useEstablishmentByUrn'
import { useSSOUserProfile } from './useSSOUser'

export type SSOCognitoIdentity = {
  id: string
  email: string
  givenName?: string
  familyName?: string
}

export type SSOConnectProfile = NonNullable<
  GetSsoUserDataQuery['getSSOUserData']
>['connectProfile']

export type ResolvedSSOProfileFields = {
  firstName?: string
  familyName?: string
  country?: string
  countryCode?: string
  phone?: string
  dob?: Date
  jobTitle?: string
  organization?: Pick<Organization, 'name' | 'urn'> &
    Partial<Pick<Organization, 'id'>>
}

/**
 * Bridges a Cognito Hosted UI (SSO) identity to a Connect profile.
 */
export const useResolveSSOProfile = () => {
  const { loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [cognitoIdentity, setCognitoIdentity] = useState<SSOCognitoIdentity>()

  useEffect(() => {
    if (authLoading) return

    Auth.currentAuthenticatedUser()
      .then((user: CognitoUser) => {
        const token = user.getSignInUserSession()?.getIdToken().payload
        const sub = token?.sub as string | undefined
        const email = token?.email as string | undefined
        const givenName = token?.given_name as string | undefined
        const familyName = token?.family_name as string | undefined
        if (!sub || !email) throw new Error('Missing Cognito session')
        setCognitoIdentity({ id: sub, email, givenName, familyName })
      })
      .catch(() => navigate('/login', { replace: true }))
  }, [authLoading, navigate])

  const {
    data,
    error,
    fetching: fetchingSSOUserData,
  } = useSSOUserProfile(cognitoIdentity?.id, cognitoIdentity?.email)

  const connectProfile = data?.getSSOUserData?.connectProfile
  const ssoProfile = data?.getSSOUserData?.ssoProfile
  const connectOrganization = connectProfile?.organizations?.[0]?.organization
  const beSmartOrganisation =
    ssoProfile?.appMemberships?.beSmart?.organisations?.[0]

  const { data: establishmentData, fetching: fetchingEstablishment } =
    useDfeByUrn(connectOrganization ? undefined : beSmartOrganisation?.urn)

  const fetching =
    !cognitoIdentity || fetchingSSOUserData || fetchingEstablishment

  const mergedFields: ResolvedSSOProfileFields = useMemo(() => {
    const dfe = establishmentData?.dfe_establishment?.[0]
    const matchedHubOrganization = dfe?.organizations?.[0]

    const dfeOrg = dfe ? { name: dfe.name, urn: dfe.urn } : undefined

    const notConnectOrg = matchedHubOrganization
      ? {
          id: matchedHubOrganization.id,
          name: matchedHubOrganization.name,
          urn: dfe?.urn,
        }
      : dfeOrg

    const organization = connectOrganization
      ? {
          id: connectOrganization.id,
          name: connectOrganization.name,
          urn: connectOrganization.urn,
        }
      : notConnectOrg

    const dob = connectProfile?.dob ?? ssoProfile?.dateOfBirth

    return {
      firstName:
        connectProfile?._given_name ?? ssoProfile?.firstName ?? undefined,
      familyName:
        connectProfile?._family_name ?? ssoProfile?.lastName ?? undefined,
      country: connectProfile?.country ?? undefined,
      countryCode: connectProfile?.countryCode ?? undefined,
      phone: connectProfile?.phone ?? ssoProfile?.phone ?? undefined,
      dob: dob ? new Date(dob) : undefined,
      jobTitle: connectProfile?.jobTitle ?? ssoProfile?.jobTitle ?? undefined,
      organization,
    }
  }, [connectOrganization, connectProfile, establishmentData, ssoProfile])

  const requiredProfileFields = [
    mergedFields.phone,
    mergedFields.dob,
    mergedFields.jobTitle,
    mergedFields.organization,
    mergedFields.firstName,
    mergedFields.familyName,
  ]
  const isProfileComplete =
    !!connectProfile && requiredProfileFields.every(Boolean)

  return {
    cognitoIdentity,
    connectProfile,
    ssoProfile,
    mergedFields,
    isProfileComplete,
    fetching,
    error,
  }
}
